import { API_BASE_URL, PERMISSIONS } from "@/config";
import { BaseService } from "@/services/base-service";

/** Default cache TTL: 20 hours (matching ITMarketplaceV2Website) */
const PERMISSIONS_CACHE_TTL_MS = 20 * 60 * 60 * 1000;

/**
 * UserPermissions — gates the app behind an authorize() call.
 *
 * authorize() calls /user-permissions via BaseService (sentry-fetch).
 * sentry-fetch handles the Midway redirect transparently, and
 * {@link import("@/services/base-service").BaseService.fetch} redirects to
 * Midway directly if sentry-fetch's silent auth path is exhausted. The
 * Lambda returns { username, allowedFeatures } after Midway authentication.
 *
 * This matches the ITMarketplaceV2Website pattern exactly, plus a
 * background reauthorization trigger on cache-miss reads.
 */
export class UserPermissions {
  public static current = new UserPermissions();

  private authenticatedUsername: string | null = null;
  private cachedPermissions: PERMISSIONS[] | null = null;
  private cacheExpiresAt = 0;
  private authorized = false;
  private authPromise: Promise<void> | null = null;

  private readonly service = new BaseService(API_BASE_URL);

  public get isAuth(): boolean {
    return !!this.authenticatedUsername;
  }

  public get username(): string | null {
    return this.authenticatedUsername;
  }

  public get hasAuthorized(): boolean {
    return this.authorized;
  }

  public get permissions(): PERMISSIONS[] {
    if (this.cachedPermissions && Date.now() < this.cacheExpiresAt) {
      return this.cachedPermissions;
    }
    return [];
  }

  public hasPermission(permission: PERMISSIONS): boolean {
    if (!this.isAuth) return false;
    if (!this.cachedPermissions || Date.now() >= this.cacheExpiresAt) {
      this.triggerReauthorization();
      return false;
    }
    return this.cachedPermissions.includes(permission);
  }

  public async hasPermissionAsync(permission: PERMISSIONS): Promise<boolean> {
    await this.authorize();
    return this.hasPermission(permission);
  }

  /**
   * Calls /user-permissions via BaseService (sentry-fetch), coalescing
   * concurrent callers onto a single in-flight request.
   * sentry-fetch handles Midway auth transparently. The Lambda returns
   * { username, allowedFeatures } from the X-FORWARDED-USER header.
   */
  public async authorize(): Promise<void> {
    if (!this.authPromise) {
      this.authPromise = this.doAuthorize().finally(() => {
        this.authPromise = null;
      });
    }
    return this.authPromise;
  }

  /**
   * Refreshes permissions in the background on a cache-miss read.
   * `BaseService.fetch` already redirects to Midway if the underlying
   * sentry-fetch call hits a dead session, so a rejection reaching here is
   * an unrelated failure (network error, non-200 response) and is only
   * logged rather than surfaced to the synchronous {@link hasPermission} caller.
   * Deduplication against other in-flight callers is handled by {@link authorize}.
   */
  private triggerReauthorization(): void {
    this.authorize().catch((error: unknown) => {
      console.error("Failed to refresh permissions in background:", error);
    });
  }

  /**
   * Performs the actual /user-permissions call. Only invoked via
   * {@link authorize}, which coalesces concurrent callers onto one promise.
   */
  private async doAuthorize(): Promise<void> {
    const response = await this.service.fetch("/user-permissions");

    if (response.status !== 200) {
      throw new Error("An error occurred when authorizing the user.");
    }

    const json = await response.json();
    if (!json.username) {
      throw new Error("Midway authentication required");
    }

    this.authenticatedUsername = json.username;
    this.cachedPermissions = (json.allowedFeatures || []) as PERMISSIONS[];
    this.cacheExpiresAt = Date.now() + PERMISSIONS_CACHE_TTL_MS;
    this.authorized = true;
  }
}
